//GetSearchResults.ts =================================================
import "../Util/Prototypes";

import * as AdvancedSearch from "./AdvancedSearch";
import * as AutoCompleteMUI from "./AutoCompleteMUI";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as NoRecordsFound from "./NoRecordsFound";
import * as QueryString from "../QueryString";
import * as QuickSearch from "./QuickSearch";
import * as Search from "./Search";
import * as SelectionMode from "./SelectionMode";
import * as SelectOnMap from "./SelectOnMap";
import * as Settings from "../Settings";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Widgets from "../Widgets/Widgets";

//ESRI javascript API.
import Point from "@arcgis/core/geometry/Point";
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

//Module variables.
let ii: number; //Index into the selectable layers array.
let jj: number; //Index into the quick search fields array.
let query: __esri.Query;
let addNextPage: boolean;
let layer: Search.iLayer; // Currently existing or new layer object.
let newLayerNames: string[]; // List of new layer names being processed.
let numChanges: number; //Number of changes for the current search.

export interface iSelectableLayers {
   name: string;     // layer name.
   fields: string[]; // Field names to search.
   sql: string[];    // SQL per field.
}
let selectableLayers: iSelectableLayers[]; // List of all layers currently being searched by any of the 3 search tools.

//Initialize select features.
export function init(id: string | number = null, address: string = null) {
   Util.logMsg("Top of GetSearchResults.init");
   if (Spinner.busy) {
      return; //Quit if busy.
   }
   Spinner.show();

   //Init.
   ii = -1;
   jj = -1;
   Search.setActivePIN("");
   Search.setMode(SelectionMode.value);
   newLayerNames = [];
   NoRecordsFound.setMsg("");
   numChanges = 0;
   QueryString.setZoomToStartExtent(false);

   if (Search.mode === "New") {
      Search.setLayers([]);
      Search.setGrandTotRec(0);
   }

   //Set layers to search.
   selectableLayers = []; //Clear layers to search.

   if (id) {
      const layerSpec: Settings.iLayer = Util_App.getLayerSpec(Search.activeLayer);
      selectableLayers.Add({ name: layerSpec.layerName, fields: [layerSpec.id_FieldName], sql: [] });

   } else if (address) {
      const layerSpec: Settings.iLayer = Util_App.getLayerSpec(Search.activeLayer);
      selectableLayers.Add({ name: layerSpec.layerName, fields: [Settings.propertyAddress_fieldName], sql: [] });

   } else if (Search.activeSearchTool === "Quick Search") {
      if (QuickSearch.layerName === "") {
         getQuickSearchSelectableLayers();
         return; //Quit because selectable layers and fields need to be determined first.

      } else { //User specified the layer and field name.
         selectableLayers.Add({ name: QuickSearch.layerName, fields: [QuickSearch.fieldName], sql: [] });
      }

   } else if (Search.activeSearchTool === "Select on Map" || Search.activeSearchTool === "") {
      if (SelectOnMap.selectableLayer === "All Visible Layers") {
         selectableLayers = Util_App.getVisibleSelectOnMapLayerNames("Select");
      } else {
         selectableLayers.Add({ name: SelectOnMap.selectableLayer, fields: [], sql: [] });
      }

   } else if (Search.activeSearchTool === "Advanced Search") {
      selectableLayers.Add({ name: AdvancedSearch.layerName, fields: [], sql: [] });
   }

   selectFeatures(id, address);
}

//Select features in all map layers.
function selectFeatures(id?: string | number, address?: string): void {
   Util.logMsg("Top of GetSearchResults.selectFeatures");
   ii++; //Process next layer.

   if (ii < selectableLayers.length) {
      addNextPage = false;
      const layerName: string = selectableLayers[ii].name;
      const curLay: Search.iLayer = findLayerByName(layerName);

      if (curLay) { //Get existing current layer.
         layer = curLay;
         layer.begRec = 0;

      } else { //Create new layer.
         const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
         layer = {
            name: layerName, spec: laySpec, fs: null, IDs: [], prevQuery: null, begRec: 0, endRec: 0,
            numRec: Settings.maxSearchRecPerPage, totRec: 0, zoomAllMBR: null,
         };
      }

      //Quit if too many records selected.
      if (SelectionMode.value === "Add") {
         if (layer.IDs.length >= Settings.maxAddRecordCount) {
            //SnackBar.show("Maximum number of " + layer.name + " records selected", 4);
            GraLay.temp.removeAll(); //Remove select on map temporary graphics.
            alert("Maximum number of " + layer.name + " records selected");
            selectFeatures();
            //setTimeout(function () { selectFeatures(); }, 500); //Wait so snack bar message will show.
            return;
         }
      }

      //Select only one feature by id or query string on startup active layer.
      if (id) {
         let sql: string = layer.spec.id_FieldName + " = '" + id + "'";
         if (layer.spec.id_FieldIsInteger) {
            sql = sql.replace(/'/g, "");
         }
         executeQueryTask(null, sql);

      } else if (address) {
         const sql: string = Settings.propertyAddress_fieldName + " = '" + address.trim() + "'";
         executeQueryTask(null, sql);

      } else if (Search.activeSearchTool === "Quick Search") {
         getQuickSearchSQL();

      } else if (Search.activeSearchTool === "Select on Map" || Search.activeSearchTool === "") {
         if (SelectOnMap.geom) {
            executeQueryTask(SelectOnMap.geom);
         } else {
            executeQueryTask(null, "1=1"); //Select all features.
         }

      } else if (Search.activeSearchTool === "Advanced Search") {
         executeQueryTask(null, AdvancedSearch.advancedSQL);
      }

   } else { //All map services have been processed.
      displayResults(); //Build graphic results.
   }
}

//Get quick search selectable layers and fields.
function getQuickSearchSelectableLayers(): void {
   Util.logMsg("Top of getQuickSearchSelectableLayers");

   //Search all autocomplete table layers and fields.
   query = new Query({
      returnGeometry: false,
      returnDistinctValues: true,
      outFields: [AutoCompleteMUI.layerField],
      orderByFields: [AutoCompleteMUI.layerField],
      where: formQuickSearchWhere(AutoCompleteMUI.suggestField),
   });

   //Execute query.
   Util.logMsg("Query for getQuickSearchData", query);
   QueryTask.executeQueryJSON(Settings.autoCompleteURL, query).then(searchForSelectableLayers_success).catch(queryTask_failed);
}

//Search for quick search selectable layers completed.
function searchForSelectableLayers_success(fs: __esri.FeatureSet): void {
   Util.logMsg("Top of GetSearchResults.searchForSelectableLayers_success");

   for (const feature of fs.features) {
      const layerDashField = feature.attributes[AutoCompleteMUI.layerField] as string;
      const layAndField = Util_App.getLayerAndFieldFromLayerDashField(layerDashField);
      const layerName = layAndField.layerName;
      const fieldName = layAndField.fieldName;
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);

      if (laySpec) { //Make sure layer spec exists.
         let found: boolean = false;
         for (const selLay of selectableLayers) {
            if (selLay.name === layerName) {
               found = true;
               break;
            }
         }

         if (found) {
            for (const selLay of selectableLayers) {
               if (selLay.name === layerName) {
                  selLay.fields.Add(fieldName);
               }
            }
         } else {
            selectableLayers.Add({ name: layerName, fields: [fieldName], sql: [] });
         }

      } else {
         Util.errMsg("Layer setting not found for " + layerName);
      }
   }

   selectFeatures();
}

//Get quick search sql for each field.
function getQuickSearchSQL() {
   jj++; //Process next field.
   let whereStr: string;

   if (jj < selectableLayers[ii].fields.length) {
      const layerName: string = selectableLayers[ii].name;
      let fieldName: string = selectableLayers[ii].fields[jj];
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
      const oneToManySpec = Util_App.getOneToManySpec(fieldName, laySpec);

      //Process one to many.
      if (oneToManySpec && fieldName === oneToManySpec[0]) {
         fieldName = oneToManySpec[3]; //Get the field name in the one to many table.
         whereStr = formQuickSearchWhere(fieldName);
         submitMultiRecSearch(whereStr, oneToManySpec); //Search multi address table or multi owner table, etc.
         return; //Quit because getting data.

      } else { //Standard query.
         selectableLayers[ii].sql[jj] = formQuickSearchWhere(fieldName);
      }

      getQuickSearchSQL();

   } else { //All fields have been processed for current layer.
      jj = -1;
      whereStr = "";

      //Combine sql here for each field.
      for (const sql of selectableLayers[ii].sql) {
         if (whereStr !== "") {
            whereStr += " or ";
         }
         whereStr += sql;
      }

      if (selectableLayers[ii].fields.length > 1) {
         whereStr = "(" + whereStr + ")";
      }

      executeQueryTask(null, whereStr);
   }
}

//Get list of ids for multi address table or multi owner table search.
function submitMultiRecSearch(whereStr: string, oneToManySpec: string[]): void {
   Util.logMsg("Top of GetSearchResults.submitMultiRecSearch");

   query = new Query({
      returnGeometry: false,
      returnDistinctValues: true,
      outFields: [oneToManySpec[4]],
      where: whereStr,
      //start: 0,
      //num: Settings.maxRecordCount, //Bug - can't be set, so max is set by the published map service setting of 1000 or so.
   });

   //Execute query.
   QueryTask.executeQueryJSON(oneToManySpec[2], query).then(getMultiRec_success).catch(queryTask_failed);
}

//ID values have been returned from server.
function getMultiRec_success(fs: __esri.FeatureSet): void {
   Util.logMsg("Top of GetSearchResults.getMultiRec_success");
   let whereStr: string;

   //No records found, so create query to return no records.
   if (fs.features.length === 0) {
      whereStr = "1=2";

   } else {  //Build where string of IDs.
      const id_field = Util_App.getLayerIDField(layer.spec);
      const sa: string[] = Util_App.buildIDList(id_field, fs);
      whereStr = Util_App.buildIDListWhere(id_field, layer.spec.id_FieldIsInteger, sa);
   }

   selectableLayers[ii].sql[jj] = whereStr;
   getQuickSearchSQL();
}

//Perform spatial and attribute query.
function executeQueryTask(geom: __esri.Geometry, sql: string = ""): void {
   Util.logMsg("Top of GetSearchResults.executeQueryTask");

   //Build query.
   query = new Query({
      returnGeometry: true, //Does not return extent for points.
      outSpatialReference: MainMap.activeView.spatialReference,
      maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
      outFields: ["*"],
   });

   if (!MainMap.activeView.spatialReference.isWebMercator) {
      query.geometryPrecision = 0;
   }

   if (geom) {
      query.geometry = geom;

      //Add a search tolerance when selecting points and lines with point geometry.
      if (geom.type === "point" && layer.spec.geometryType !== "polygon") {
         query.distance = Util_App.getSelectByPointTolerance(true);

         query.units = "feet"; //Units is required for some bug reason.
         if (MainMap.activeView.spatialReference.isWebMercator) {
            query.units = "meters";
         }
      }
   }

   if (sql) {
      query.where = sql;
   }

   if (layer.spec.sortBy !== "") {
      query.orderByFields = [layer.spec.sortBy];
   }

   //Only select records to be added or removed from current selection.
   if (((Search.mode === "Add") || (Search.mode === "Remove")) && (layer.IDs.length > 0)) {
      const idListWhere = Util_App.buildIDListWhere(layer.spec.id_FieldName, layer.spec.id_FieldIsInteger, layer.IDs);

      if (sql) {
         query.where += " and ";
      } else {
         query.where = "";
      }

      if (Search.mode === "Add") {
         query.where += idListWhere.replace(/in \(/g, "not in (");

      } else { //"Remove"
         query.where += idListWhere;
      }
   }

   //Execute query for total record count.
   layer.prevQuery = query.clone(); //Save query for next button processing, etc.

   if (Search.mode === "New") {
      const url: string = Util_App.getLayerURL(layer.spec);
      QueryTask.executeForCount(url, query).then(queryTaskForCount_success).catch(queryTask_failed);

   } else { // "Add" or "Remove".
      executeStandardQueryTask();
   }
}

//Query task for total count completed event.
function queryTaskForCount_success(count: number): void {
   Util.logMsg("Top of GetSearchResults.queryTaskForCount_success. Count: " + count.toString());
   layer.totRec = count;
   executeStandardQueryTask();
}

//Standard query task (not for total count.
function executeStandardQueryTask() {
   Util.logMsg("Top of GetSearchResults.executeStandardQueryTask");

   layer.prevQuery.num = Settings.maxRecordCount; //Maximum records to select.
   layer.prevQuery.start = 0;
   const url: string = Util_App.getLayerURL(layer.spec);
   QueryTask.executeQueryJSON(url, layer.prevQuery).then(queryTaskSpatial_success).catch(queryTask_failed);
}

//Standard query task completed event.
function queryTaskSpatial_success(fs: __esri.FeatureSet): void {
   Util.logMsg("Top of GetSearchResults.queryTaskSpatial_success");
   GraLay.temp.removeAll(); //Remove select on map temporary graphics.

   if (Search.mode === "Add") {
      layer.totRec = layer.IDs.length + fs.features.length;

   } else if (Search.mode === "Remove") {
      layer.totRec = Math.max(layer.IDs.length - Math.min(fs.features.length, Settings.maxRecordCount), 0);
   }

   layer.endRec = Math.min(layer.totRec, Settings.maxRecordCount) - 1;
   layer.numRec = Math.min(layer.totRec, Settings.maxSearchRecPerPage);
   processQueryResults(fs);
}

//Next button was clicked.
export function executeNextQueryTask(): void {
   Util.logMsg("Top of GetSearchResults.executeNextQueryTask");
   layer = Search.layers[Search.activeLayerIndex];
   layer.prevQuery.start += Settings.maxRecordCount;
   const url: string = Util_App.getLayerURL(layer.spec);
   QueryTask.executeQueryJSON(url, layer.prevQuery).then(queryTaskNextSpatial_success).catch(queryTask_failed);
}

//Next button query task completed event.
function queryTaskNextSpatial_success(fs: __esri.FeatureSet) {
   Util.logMsg("Top of GetSearchResults.queryTaskNextSpatial_success");
   addNextPage = true;
   processQueryResults(fs);
}

//Process query results.
function processQueryResults(fs: __esri.FeatureSet) {
   Util.logMsg("Top of GetSearchResults.processQueryResults - Search:", Search);
   let j: number;

   //Set id field name.
   let id_field: string = Util_App.getLayerIDField(layer.spec);
   if (fs.fields) {
      id_field = Util_App.getFieldNameCase(id_field, fs.fields);
      if (!id_field) {
         Util.errMsg("Layer ID Field not found in Settings", layer.spec);
      }
   }

   //Append or remove features from existing results.
   if (fs.features.length > 0) { //New features were found.
      newLayerNames.Add(layer.name); //Keep track of layers being processed to set active layer.

      if (layer.fs) { //Means there are existing features.

         if (Search.mode === "Add" || addNextPage) {
            numChanges += fs.features.length; //Add up total of new features found.
            layer.fs.features.push(...fs.features); //Add new features to existing.

         } else if (Search.mode === "Remove") {
            const id_List = Util_App.buildIDList(id_field, fs); //Build list of id's to remove.

            //Build list of indexes to remove.
            const index_List: number[] = [];
            let k: number = 0;
            for (const feature of layer.fs.features) {
               const fieldValue = Util_App.getFieldValueFAT(id_field, feature);

               for (j = 0; j < id_List.length; j++) {
                  const id = id_List[j];
                  if (id === fieldValue) {
                     index_List.Add(k);
                     break;
                  }
               }
               k++;
            }

            numChanges += index_List.length; //Add up total of new features removed.

            //Remove each index.
            for (let i = index_List.length - 1; i >= 0; i--) {
               layer.fs.features.RemoveAt(index_List[i]);
            }
         }

      } else { //No existing features, so save new features.
         if (Search.mode !== "Remove") {
            numChanges += fs.features.length; //Add up total of new features found.
            layer.fs = fs; //Save new features for no previous data.
         }

      } //End if there are existing features.

   } else { //No result found for this layer.

   } //End if there are new features.

   //Save results.
   if (layer.fs && layer.fs.features.length > 0 && fs.features.length > 0) {
      //Sort results.
      if (layer.spec.sortBy !== "") {
         layer.fs.features.sort(Util_App.sortFC(layer.spec.sortBy));
      }

      //Build unique id list.
      layer.IDs = Util_App.buildIDList(id_field, layer.fs);

      //Set active pin.
      if (layer.name === Settings.parcelLayerName && layer.totRec === 1) {
         Search.setActivePIN(layer.IDs[0]);
      }

      //Add new layers to the master search array results.
      const lay: Search.iLayer = findLayerByName(layer.name);
      if (!lay) {
         Search.layers.Add(layer);
      }

   } else { //Remove the layer if empty.
      for (let i = 0; i < Search.layers.length; i++) {
         if (Search.layers[i].name === layer.name) {
            if (Search.layers[i].fs.features.length === 0) {
               Search.layers.RemoveAt(i);
            }
            break;
         }
      }
   }

   //Process next selectable layer.
   selectFeatures();
}

//Display results.
export function displayResults(computeLayerIndex: boolean = true): void {
   Util.logMsg("Top of GetSearchResults - displayResults");

   GraLay.clearHoverGraphics();
   GraLay.clearSelectHoverGraphics();

   //Compute grand total.
   Search.setGrandTotRec(0);
   for (const lay of Search.layers) {
      Search.setGrandTotRec(Search.grandTotRec + lay.totRec);
   }

   //No records found.
   if (Search.grandTotRec === 0) {
      clearAllResults();
      Spinner.hide();

      if (Search.activeSearchTool) {
         if (DrawerMenu.curToolName === "Search Results") {
            DrawerMenu.prevToolClick(DrawerMenu.prevToolName);

         } else {
            DrawerMenu.showTool(Search.activeSearchTool);
         }

         if (DrawerMenu.curToolName !== "Search Results") {
            let msg: string = "No results found.";
            if (SelectionMode.value === "Remove") {
               msg += ' Change the selection mode to "New" or "Add"';
            }
            NoRecordsFound.setMsg(msg);
         }
      }

      GraLay.temp.removeAll(); //Remove select on map temporary graphics.
      return;
   }

   //Util.errMsg("Search Results", Search.layers);

   //Compute the active layer index.
   if (!addNextPage && computeLayerIndex) {
      setActiveLayerIndex();
   }
   addNextPage = false;

   //Highlight features on map.
   GraLay.clearSelectHighlightGraphics();

   for (const lay of Search.layers) {
      const fs = lay.fs;

      //Set zoom to layer map extent.
      lay.zoomAllMBR = Util_App.getMBRFeatureSet(fs);
      const sym = Util_App.getHighlightSymbol(fs, "Search");
      let j: number = 0;

      for (let i = lay.begRec; i <= lay.endRec; i++) {
         const gra = fs.features[i];

         if (gra && gra.geometry) {
            j++;
            gra.symbol = sym; //Set symbology of selected feature.

            if (fs.geometryType === "point") {
               GraLay.selectedPoint.add(gra);

            } else if (fs.geometryType === "polyline") {
               GraLay.selectedLine.add(gra);

            } else { //polygon.
               GraLay.selectedPoly.add(gra);
            }

            //===================================================
            //symId.text = (j + 1).toString();
            //const graId = new Graphic({ geometry: gra.geometry.centroid, symbol: symId });
            //GraLay.selectedPoly.add(graId);
            //======================================================================
            //GraLay.selectedPoly.addMany(fs.features);
         }
      }
   } //end for all result layers.

   //Zoom to map extent.
   let mbr = Search.layers[Search.activeLayerIndex].zoomAllMBR.clone();

   if (numChanges > 0) {
      let animate: boolean = false; //Custom!
      if (MainMap.map.basemap.baseLayers.at(0).type === "wmts") {
         animate = false; //Don't animate slow basemaps.
      }

      if (Search.activeSearchTool === "Select on Map" || Search.activeSearchTool === "") {
         const extent = MainMap.activeView.extent;
         const pntLL = new Point({ x: extent.xmin, y: extent.ymin, spatialReference: MainMap.activeView.spatialReference });
         const pntUR = new Point({ x: extent.xmax, y: extent.ymax, spatialReference: MainMap.activeView.spatialReference });
         const screenDist: number = pntLL.distance(pntUR);
         const moveDist: number = mbr.center.distance(MainMap.activeView.center);

         if (moveDist < screenDist) { //Don't move too far.
            MainMap.activeView.goTo(mbr.center, { animate: animate, duration: 0 });
         }

      } else {
         mbr = Util_App.addMarginToMBR(mbr, true);
         MainMap.activeView.goTo(mbr, { animate: animate, duration: 0 });
      }

      setTimeout(function () { saveLastExtent(); }, 1100); //Save the last zoom to extent for print tool.
   }

   //Show left panel attribute results.
   if (numChanges > 0) {
      if (DrawerMenu.curToolName === "Search Results") {
         Widgets.show(DrawerMenu.curToolName);

      } else { //Search was the current tool.
         if (Search.activeSearchTool === "") {
            Spinner.hide();
         }
         DrawerMenu.showTool("Search Results");
      }

   } else {
      if (Search.activeSearchTool) {
         DrawerMenu.showTool(Search.activeSearchTool);
         const numRec: string = Util.formatTextValue(Search.grandTotRec, "Comma");
         let msg: string = "No results found.  There are " + numRec + " existing results.";
         if (SelectionMode.value === "Remove") {
            msg += ' The selection mode is "Remove".';
         }
         NoRecordsFound.setMsg(msg);
      }
      GraLay.temp.removeAll(); //Remove select on map temporary graphics.
      Spinner.hide();
   }
}

//Save the last extent after search for print tool.
function saveLastExtent() {
   Search.setExtentAfterSearch(MainMap.activeView.extent);
}

//Set active layer index.
export function setActiveLayerIndex() {
   let j: number;
   let layIndex: number = 0;
   let found: boolean = false;

   if (Search.activeSearchTool === "Quick Search") {
      if (QuickSearch.layerName === "") {
         for (const newLayerName of newLayerNames) {
            for (let i = 0; i < Search.layers.length; i++) {
               if (newLayerName === Search.activeLayer && Search.layers[i].name === Search.activeLayer) {
                  found = true;
                  layIndex = i; //Set to active layer.
                  break;
               }
            }
         }

      } else { //Set to user specified layer.
         for (let i = 0; i < Search.layers.length; i++) {
            if (Search.layers[i].name === QuickSearch.layerName) {
               found = true;
               layIndex = i;
               break;
            }
         }
      }

   } else if (Search.activeSearchTool === "Select on Map" || Search.activeSearchTool === "") {
      if (SelectOnMap.selectableLayer !== "All Visible Layers") {
         for (let i = 0; i < Search.layers.length; i++) {
            if (Search.layers[i].name === SelectOnMap.selectableLayer) {
               found = true;
               layIndex = i; //Set to user specified layer.
               break;
            }
         }

      } else if (Search.activeSearchTool === "Select on Map") {
         for (let i = 0; i < Search.layers.length; i++) {
            if (Search.layers[i].name === Search.activeLayer && Search.layers[i].totRec > 1) {
               found = true;
               layIndex = i; //Set to active layer only if more than 1 record found.
               break;
            }
         }
      }
   }

   //Set to first point, line, or polygon.
   if (!found) {
      j = getLayerIndex("point");
      if (j !== -1) {
         found = true;
         layIndex = j;
      }

      if (!found) {
         j = getLayerIndex("polyline");
         if (j !== -1) {
            found = true;
            layIndex = j;
         }

         if (!found) {
            for (let i = 0; i < Search.layers.length; i++) {
               if (Search.layers[i].name === Search.activeLayer) {
                  found = true;
                  layIndex = i; //Set to active layer.
                  break;
               }
            }

            if (!found) {
               j = getLayerIndex("polygon");
               if (j !== -1) {
                  found = true;
                  layIndex = j;
               }
            }

         } // polygon.
      } // line.
   } // point.

   Util_App.setActiveLayer(Search.layers[layIndex].name);
}

//Find matching geometry type to set active layer index.
function getLayerIndex(geomType: string): number {
   let layIndex: number = -1;

   for (const newLayerName of newLayerNames) {
      for (let i = 0; i < Search.layers.length; i++) {
         if (Search.layers[i].fs.geometryType === geomType && newLayerName === Search.layers[i].name) {
            layIndex = i;
            break;
         }
      }
      if (layIndex !== -1) {
         break;
      }
   }
   return layIndex;
}

//Get default map tip content html.
export function getMapTipContentHTML(layerName: string, feature: __esri.Graphic, fs: __esri.FeatureSet) {
   let html: string = "";

   const styles: Default.iStyles = {
      rowStyle1: { fontWeight: 500, color: 'maroon' },
      rowStyle2: { fontWeight: 500 },
   };
   let rowStyle: any = styles.rowStyle1;

   const fields: Util_App.iField[] = Util_App.getHeadingValues(fs, feature, layerName);

   for (let i = 0; i < fields.length; i++) {
      const field: Util_App.iField = fields[i];

      if (i > 0) {
         rowStyle = styles.rowStyle2;
      }

      if (Settings.security.accessLevel >= field.accessLevel) {
         html += '<span style="' + rowStyle + '">' + field.alias + ':</span>&nbsp;&nbsp;';
         html += '<span style="' + rowStyle + '">' + field.value + '</span><br />';
      }
   }
   return html;
}

//Form search where query string.
function formQuickSearchWhere(fieldName: string): string {
   Util.logMsg("Top of GetSearchResults.formQuickSearchWhere");
   let sql: string = "";
   const searchText: string = QuickSearch.searchText.trim();

   const firstChar: string = searchText.charAt(0);
   const lastChar: string = searchText.charAt(searchText.length - 1);
   const startsWithNumber: boolean = firstChar.IsNumeric();
   const endsWithNumber: boolean = lastChar.IsNumeric();

   let isInteger: boolean = false;
   if (layer) {
      isInteger = Util_App.getIsIntegerByFieldName(layer.name, fieldName);
   }

   //Build sql.
   if (searchText === "") {
      sql = "1=1";

      //Integer value.
   } else if (isInteger) {
      sql = Util_App.buildQueryClause(fieldName, "=", Util.parseFlt(searchText).toString(), "integer");

      //Use the exact autosuggest value.
   } else if (QuickSearch.exactSearch) {
      if (searchText.length < 50) {
         sql = Util_App.buildQueryClause(fieldName, "=", searchText);
      } else {
         sql = Util_App.buildQueryClause(fieldName, "begins with", searchText);
      }

      //Starts with number or search for PIN.
   } else if ((startsWithNumber && endsWithNumber) || (fieldName === Settings.pin_fieldName)) {
      sql = Util_App.buildQueryClause(fieldName, "begins with", searchText);

   } else { //Seach by individual words.
      sql = Util_App.buildQueryClause(fieldName, "contains all the words", searchText);
   }

   return sql;
}

//Clear all results.
export function clearAllResults(clearErrorPanel: boolean = false): void {
   Util.logMsg("Top of GetSearchResults.clearAllResults");

   Search.setLayers([]);
   Search.setGrandTotRec(0);
   Search.setActivePIN("");
   GraLay.clearSelectHighlightGraphics();
}

//Find existing selectable layer by layer name.
function findLayerByName(layerName: string): Search.iLayer {
   for (const lay of Search.layers) {
      if (lay.name === layerName) {
         return lay;
      }
   }
   return null;
}

//Query task failed.
function queryTask_failed(error: Error): void {
   let msg = "QueryTask failed.\n ";
   if (query.where) {
      msg += "Where: " + query.where + " ";
   }
   if (query.outFields) {
      msg += ", Output fields: " + query.outFields.toString();
   }
   if (query.orderByFields) {
      msg += ", OrderBy fields: " + query.orderByFields.toString();
   }
   errMsg(msg, error);
}

//Show error message.
function errMsg(msg: string, error?: object): void {
   if (error) {
      Util.errMsg(msg, error);
   } else {
      Util.errMsg(msg);
   }
   clearAllResults();
   Spinner.hide();
}
